import { Card } from "@/components/ui/Card";
import { OnlineBookingFieldModesEditor } from "@/pages/stammdaten/OnlineBookingFieldModesEditor";
import { OnlineBookingFormHint } from "@/pages/stammdaten/online-booking/OnlineBookingFormHint";
import type { OnlineBookingStreckeSub } from "@/pages/stammdaten/online-booking/onlineBookingSubSections";
import type { OnlineBookingSettings } from "@/pages/stammdaten/online-booking/useOnlineBookingSettings";

type Props = Pick<
  OnlineBookingSettings,
  | "flow"
  | "setFlow"
  | "primaryGuestFields"
  | "setPrimaryGuestFields"
  | "companionFieldsAdult"
  | "setCompanionFieldsAdult"
  | "companionFieldsChild"
  | "setCompanionFieldsChild"
> & {
  canWrite: boolean;
  subSection: OnlineBookingStreckeSub;
};

export function OnlineBookingStreckePanel({
  canWrite,
  subSection,
  flow,
  setFlow,
  primaryGuestFields,
  setPrimaryGuestFields,
  companionFieldsAdult,
  setCompanionFieldsAdult,
  companionFieldsChild,
  setCompanionFieldsChild,
}: Props) {
  if (subSection === "schritte") {
    return (
      <Card
        title="Schritte in der Buchungsstrecke"
        description="Legt fest, welche Optionen und Zusatzabfragen Gäste während der Buchung sehen."
        bodyClassName="space-y-3"
      >
        <OnlineBookingFormHint>
          Die Reihenfolge der Schritte ist fest: Zeitraum → Kategorie → Details → Kontakt →
          Zusatzartikel → Absenden. Hier steuern Sie nur, welche Felder innerhalb dieser Schritte
          erscheinen.
        </OnlineBookingFormHint>
        <fieldset className="space-y-2" disabled={!canWrite}>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1 shrink-0"
              checked={flow.show_electricity_option}
              onChange={(e) =>
                setFlow((f) => ({ ...f, show_electricity_option: e.target.checked }))
              }
            />
            <span>Strom-Option im Zeitraum-Schritt anzeigen</span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1 shrink-0"
              checked={flow.show_children_field}
              onChange={(e) => setFlow((f) => ({ ...f, show_children_field: e.target.checked }))}
            />
            <span>
              Kinder-Anzahl im Details-Schritt
              <span className="mt-0.5 block text-xs text-label-secondary">
                Beeinflusst die Preisberechnung, wenn Kinderpreise in Ihren Artikeln hinterlegt
                sind.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1 shrink-0"
              checked={flow.show_dogs_field}
              onChange={(e) => setFlow((f) => ({ ...f, show_dogs_field: e.target.checked }))}
            />
            <span>
              Hunde-Anzahl im Details-Schritt
              <span className="mt-0.5 block text-xs text-label-secondary">
                Optional, wenn Hundegebühren über Artikel und Buchungszuordnung abgerechnet
                werden.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1 shrink-0"
              checked={flow.show_companion_details}
              onChange={(e) =>
                setFlow((f) => ({ ...f, show_companion_details: e.target.checked }))
              }
            />
            <span>
              Mitreisende im Kontakt-Schritt abfragen
              <span className="mt-0.5 block text-xs text-label-secondary">
                Wenn aktiv, werden für jeden weiteren Erwachsenen und jedes Kind separate
                Formulare angezeigt. Welche Felder abgefragt werden, legen Sie unter{" "}
                <strong>Gastdaten & Felder</strong> fest.
              </span>
            </span>
          </label>
        </fieldset>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card
        title="Hauptreisende/r – Felder"
        description="Welche Angaben der Hauptreisende ausfüllen muss, optional angeben kann oder nicht abgefragt wird."
        bodyClassName="space-y-4"
      >
        <OnlineBookingFormHint>
          <strong>Aus</strong> = Feld wird nicht angezeigt. <strong>Optional</strong> = Gäste
          können es leer lassen. <strong>Pflicht</strong> = Buchung ist ohne Angabe nicht möglich.
          Name und E-Mail sind für die Buchung in der Regel erforderlich.
        </OnlineBookingFormHint>
        <OnlineBookingFieldModesEditor
          fields={primaryGuestFields}
          onChange={setPrimaryGuestFields}
          canWrite={canWrite}
          namePrefix="primary-guest"
        />
      </Card>

      <Card
        title="Mitreisende – Felder (Erwachsene)"
        description="Angaben für jeden weiteren Erwachsenen ab Person 2 – nur relevant, wenn „Mitreisende abfragen“ aktiv ist."
        bodyClassName="space-y-4"
      >
        <OnlineBookingFieldModesEditor
          fields={companionFieldsAdult}
          onChange={setCompanionFieldsAdult}
          canWrite={canWrite}
          namePrefix="companion-adult"
        />
      </Card>

      <Card
        title="Mitreisende – Felder (Kinder)"
        description="Angaben für jedes mitreisende Kind – nur relevant, wenn Kinder gezählt und Mitreisende abgefragt werden."
        bodyClassName="space-y-4"
      >
        <OnlineBookingFieldModesEditor
          fields={companionFieldsChild}
          onChange={setCompanionFieldsChild}
          canWrite={canWrite}
          namePrefix="companion-child"
        />
      </Card>
    </div>
  );
}
